/**
 * Unified Sampling Service
 *
 * Provides consistent session-based sampling across all performance monitoring:
 * - Web Vitals (LCP, INP, CLS) with metric-specific rates
 * - TTI (Time To Interactive) for soft navigations
 * - Resource Monitoring
 * - Network Diagnostics
 *
 * Environment-based sampling rates:
 * - Development/Staging: 100% (all metrics reported for debugging)
 * - Production: 2% for all metrics
 *
 * Benefits:
 * - Correlation: Sampled sessions get correlated metrics for easier debugging
 * - Single control point: One place to adjust sampling rates
 * - Environment-aware: Full reporting in dev/staging, sampled in production
 *
 * Always-Report Rules (bypass sampling):
 * - Poor ratings (LCP > 4s, INP > 500ms, CLS > 0.25, TTI > 5s)
 * - Failed requests (HTTP 4xx/5xx)
 * - Slow requests (above threshold)
 * - Large responses (above size threshold)
 */

// ============================================================================
// Environment Detection
// ============================================================================

/**
 * Check if we're in a non-production environment (dev, staging, beta)
 * In non-production, we want 100% sampling for easier debugging
 */
const isNonProductionEnvironment = (): boolean => {
  const env = process.env.NODE_ENV?.toLowerCase() || "development";
  return env !== "production";
};

// ============================================================================
// Configuration
// ============================================================================

export interface SamplingConfig {
  // ---- Global sampling (report everything for sampled sessions) ----
  /** Global sample rate (0-1). When a session is globally sampled, ALL metrics are reported */
  globalSampleRate: number;

  // ---- Metric-specific sample rates (0-1) ----
  /** LCP sample rate (1 = 100%, report all) */
  lcpSampleRate: number;
  /** INP sample rate */
  inpSampleRate: number;
  /** CLS sample rate */
  clsSampleRate: number;
  /** Resource monitoring sample rate */
  resourceSampleRate: number;
  /** Network diagnostics sample rate */
  networkSampleRate: number;
  /** TTI (Time To Interactive) sample rate for soft navigations */
  ttiSampleRate: number;

  // ---- Always-report rules ----
  /** Always report web vitals with 'poor' rating regardless of sampling */
  alwaysReportPoorRatings: boolean;

  /** Always report failed HTTP requests (4xx/5xx) regardless of sampling */
  alwaysReportFailures: boolean;

  /** Always report slow resources regardless of sampling */
  alwaysReportSlowResources: boolean;

  /** Threshold for slow resources (ms) */
  slowResourceThresholdMs: number;

  /** Always report large API responses regardless of sampling */
  alwaysReportLargeResponses: boolean;

  /** Size threshold for API responses (KB) */
  largeApiResponseThresholdKB: number;

  /** Size threshold for assets like JS/CSS/images (KB) */
  largeAssetThresholdKB: number;
}

// Production sampling rates (2% for all metrics)
const PRODUCTION_CONFIG: SamplingConfig = {
  globalSampleRate: 0.02, // 2% of sessions get full reporting
  lcpSampleRate: 0.02, // 2%
  inpSampleRate: 0.02, // 2%
  clsSampleRate: 0.02, // 2%
  resourceSampleRate: 0.02, // 2%
  networkSampleRate: 0.02, // 2%
  ttiSampleRate: 0.02, // 2%
  alwaysReportPoorRatings: true,
  alwaysReportFailures: true,
  alwaysReportSlowResources: true,
  slowResourceThresholdMs: 3000,
  alwaysReportLargeResponses: true,
  largeApiResponseThresholdKB: 50,
  largeAssetThresholdKB: 500,
};

// Dev/Staging sampling rates (100% for all metrics - easier debugging)
const DEV_STAGING_CONFIG: SamplingConfig = {
  globalSampleRate: 1, // 100% - report everything
  lcpSampleRate: 1, // 100%
  inpSampleRate: 1, // 100%
  clsSampleRate: 1, // 100%
  resourceSampleRate: 1, // 100%
  networkSampleRate: 1, // 100%
  ttiSampleRate: 1, // 100%
  alwaysReportPoorRatings: true,
  alwaysReportFailures: true,
  alwaysReportSlowResources: true,
  slowResourceThresholdMs: 3000,
  alwaysReportLargeResponses: true,
  largeApiResponseThresholdKB: 50,
  largeAssetThresholdKB: 500,
};

/**
 * Get the default config based on environment
 * - Development/Staging/Beta: 100% sampling for debugging
 * - Production: 2% sampling to reduce volume
 */
const getDefaultConfig = (): SamplingConfig => {
  return isNonProductionEnvironment() ? DEV_STAGING_CONFIG : PRODUCTION_CONFIG;
};

const DEFAULT_CONFIG: SamplingConfig = getDefaultConfig();

// Allow runtime config override
let config: SamplingConfig = { ...DEFAULT_CONFIG };

// ============================================================================
// Debug Mode (enable 100% sampling via localStorage)
// ============================================================================
// To enable in production, run in DevTools console:
//   localStorage.setItem('perf_debug', 'true'); location.reload();
// To disable:
//   localStorage.removeItem('perf_debug'); location.reload();

const checkDebugMode = (): void => {
  try {
    if (
      typeof window !== "undefined" &&
      localStorage.getItem("perf_debug") === "true"
    ) {
      config = {
        ...config,
        globalSampleRate: 1,
        ttiSampleRate: 1,
        lcpSampleRate: 1,
        inpSampleRate: 1,
        clsSampleRate: 1,
        resourceSampleRate: 1,
        networkSampleRate: 1,
      };
      console.log(
        "[PerfMonitor] Debug mode enabled - 100% sampling for all metrics"
      );
    }
  } catch {
    // localStorage not available (SSR or restricted)
  }
};

// Check debug mode on load
checkDebugMode();

/** Update sampling configuration */
export const updateSamplingConfig = (
  newConfig: Partial<SamplingConfig>
): void => {
  config = { ...config, ...newConfig };
};

/** Get current sampling configuration */
export const getSamplingConfig = (): SamplingConfig => ({ ...config });

// ============================================================================
// Session Sampling Decisions (computed once per session per metric type)
// ============================================================================

/** Session sampling decisions - computed once per metric type and cached */
interface SessionSamplingDecisions {
  global: boolean | null; // When true, report everything
  lcp: boolean | null;
  inp: boolean | null;
  cls: boolean | null;
  resource: boolean | null;
  network: boolean | null;
  tti: boolean | null;
}

const sessionSampling: SessionSamplingDecisions = {
  global: null,
  lcp: null,
  inp: null,
  cls: null,
  resource: null,
  network: null,
  tti: null,
};

/** Check if this session is globally sampled (reports everything) */
export const isGloballySampled = (): boolean => {
  if (sessionSampling.global === null) {
    sessionSampling.global = Math.random() < config.globalSampleRate;
  }
  return sessionSampling.global;
};

/** Check if LCP is sampled for this session */
export const isLCPSampled = (): boolean => {
  // Global sampling overrides metric-specific sampling
  if (isGloballySampled()) return true;

  if (sessionSampling.lcp === null) {
    sessionSampling.lcp = Math.random() < config.lcpSampleRate;
  }
  return sessionSampling.lcp;
};

/** Check if INP is sampled for this session */
export const isINPSampled = (): boolean => {
  // Global sampling overrides metric-specific sampling
  if (isGloballySampled()) return true;

  if (sessionSampling.inp === null) {
    sessionSampling.inp = Math.random() < config.inpSampleRate;
  }
  return sessionSampling.inp;
};

/** Check if CLS is sampled for this session */
export const isCLSSampled = (): boolean => {
  // Global sampling overrides metric-specific sampling
  if (isGloballySampled()) return true;

  if (sessionSampling.cls === null) {
    sessionSampling.cls = Math.random() < config.clsSampleRate;
  }
  return sessionSampling.cls;
};

/** Check if resources are sampled for this session */
export const isResourceSampled = (): boolean => {
  // Global sampling overrides metric-specific sampling
  if (isGloballySampled()) return true;

  if (sessionSampling.resource === null) {
    sessionSampling.resource = Math.random() < config.resourceSampleRate;
  }
  return sessionSampling.resource;
};

/** Check if network diagnostics are sampled for this session */
export const isNetworkSampled = (): boolean => {
  // Global sampling overrides metric-specific sampling
  if (isGloballySampled()) return true;

  if (sessionSampling.network === null) {
    sessionSampling.network = Math.random() < config.networkSampleRate;
  }
  return sessionSampling.network;
};

/** Check if TTI is sampled for this session */
export const isTTISampled = (): boolean => {
  // Global sampling overrides metric-specific sampling
  if (isGloballySampled()) return true;

  if (sessionSampling.tti === null) {
    sessionSampling.tti = Math.random() < config.ttiSampleRate;
  }
  return sessionSampling.tti;
};

/** Reset all session sampling decisions (for testing or new session) */
export const resetSessionSampling = (): void => {
  sessionSampling.global = null;
  sessionSampling.lcp = null;
  sessionSampling.inp = null;
  sessionSampling.cls = null;
  sessionSampling.resource = null;
  sessionSampling.network = null;
  sessionSampling.tti = null;
};

// ============================================================================
// Web Vitals Sampling
// ============================================================================

export type WebVitalRating = "good" | "needs-improvement" | "poor";
export type WebVitalMetric = "LCP" | "INP" | "CLS";

/**
 * Determine if a web vital metric should be reported
 * @param metric - The metric type ('LCP', 'INP', 'CLS')
 * @param rating - The metric's rating ('good', 'needs-improvement', 'poor')
 * @returns true if the metric should be reported
 */
export const shouldReportWebVital = (
  metric: WebVitalMetric,
  rating: string
): boolean => {
  // Always report poor ratings
  if (config.alwaysReportPoorRatings && rating === "poor") {
    return true;
  }

  // Use metric-specific sampling rates
  switch (metric) {
    case "LCP":
      return isLCPSampled();
    case "INP":
      return isINPSampled();
    case "CLS":
      return isCLSSampled();
    default:
      return false;
  }
};

// ============================================================================
// Resource Sampling
// ============================================================================

export interface ResourceSamplingInput {
  url: string;
  durationMs: number;
  httpStatus: number;
  transferSizeBytes: number;
  initiatorType: string; // 'fetch', 'xmlhttprequest', 'script', 'img', etc.
}

export interface ResourceSamplingResult {
  shouldReport: boolean;
  reason?: "session-sampled" | "failed" | "slow" | "large" | "skipped";
}

/** URLs to always skip (our own monitoring endpoints) */
const SKIP_URL_PATTERNS = [
  "resource-performance-logs",
  "page-load-performance-logs",
  "promise-executor-logs",
  "firestore.googleapis.com",
];

/**
 * Determine if a resource should be reported
 * @param input - Resource details
 * @returns Result with shouldReport flag and reason
 */
export const shouldReportResource = (
  input: ResourceSamplingInput
): ResourceSamplingResult => {
  const { url, durationMs, httpStatus, transferSizeBytes, initiatorType } =
    input;

  // Skip our own monitoring endpoints
  if (SKIP_URL_PATTERNS.some((pattern) => url.includes(pattern))) {
    return { shouldReport: false, reason: "skipped" };
  }

  // Always report failures
  if (config.alwaysReportFailures && httpStatus >= 400) {
    return { shouldReport: true, reason: "failed" };
  }

  // Always report slow resources
  if (
    config.alwaysReportSlowResources &&
    durationMs > config.slowResourceThresholdMs
  ) {
    return { shouldReport: true, reason: "slow" };
  }

  // Always report large responses
  if (config.alwaysReportLargeResponses) {
    const isApi =
      initiatorType === "fetch" || initiatorType === "xmlhttprequest";
    const thresholdBytes = isApi
      ? config.largeApiResponseThresholdKB * 1024
      : config.largeAssetThresholdKB * 1024;

    if (transferSizeBytes > thresholdBytes) {
      return { shouldReport: true, reason: "large" };
    }
  }

  // Otherwise, respect session sampling for resources
  if (isResourceSampled()) {
    return { shouldReport: true, reason: "session-sampled" };
  }

  return { shouldReport: false, reason: "skipped" };
};

// ============================================================================
// Network Diagnostics Sampling
// ============================================================================

/**
 * Determine if network diagnostics should be reported
 * Uses network-specific session sampling
 */
export const shouldReportNetworkDiagnostics = (): boolean => {
  return isNetworkSampled();
};

// ============================================================================
// TTI Sampling
// ============================================================================

export type TTIRating = "good" | "needs-improvement" | "poor";

/**
 * Determine if TTI should be reported
 * @param rating - The TTI rating ('good', 'needs-improvement', 'poor')
 * @returns true if the TTI should be reported
 */
export const shouldReportTTI = (rating: TTIRating): boolean => {
  // Always report poor TTI (slow navigations)
  if (config.alwaysReportPoorRatings && rating === "poor") {
    return true;
  }

  // Use TTI-specific session sampling
  return isTTISampled();
};
